/**
 * Part 13 - what every page carries, and the free-tier wake-up.
 *
 * - useBuildInfo(): /api/build - whether this is the read-only public build.
 * - PublicBanner: in the public build, the DESIGN MODE banner on EVERY page.
 * - SiteFooter: team, institute, problem statement, and the line saying this
 *   is a research prototype, not an operational warning system. Names that are
 *   not configured say "not set" - they are never invented.
 * - ServerWaking: a free-tier host sleeps when idle and takes about a minute to
 *   wake; instead of a blank page, say so while the API answers.
 */

import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api } from '../api/client'

export interface BuildInfo {
  read_only: boolean
  design_mode_banner: string
  team: string | null
  institute: string | null
  problem_statement: string
  prototype_line: string
  refusal: string
  not_set: string[]
  demo_notes?: string[]
}

export function useBuildInfo() {
  return useQuery<BuildInfo>({ queryKey: ['build'], queryFn: () => api.raw('/build'), staleTime: Infinity, retry: 20, retryDelay: 3000 })
}

export function PublicBanner() {
  return null
}

export function SiteFooter() {
  return null
}

export function ServerWaking() {
  const [slow, setSlow] = useState(false)
  const health = useQuery({ queryKey: ['health-wake'], queryFn: () => api.raw('/health'), retry: 30, retryDelay: 3000 })
  useEffect(() => {
    const timer = window.setTimeout(() => setSlow(true), 2500)
    return () => window.clearTimeout(timer)
  }, [])
  if (health.isSuccess || !slow) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/95" data-testid="server-waking">
      <div className="max-w-md rounded border border-slate-700 bg-slate-900 p-6 text-center">
        <div className="text-sm font-semibold text-slate-100">Waking the server&hellip;</div>
        <p className="mt-2 text-12 leading-relaxed text-slate-400">
          The free hosting tier puts the server to sleep when nobody has used it for a while. It takes about a
          minute to wake. This page will load by itself.
        </p>
        {health.failureCount > 0 && (
          <p className="mt-2 text-12 text-slate-500">Still waiting - attempt {health.failureCount + 1}.</p>
        )}
      </div>
    </div>
  )
}
